import React, { useEffect, useState } from 'react';
import { Copy, ExternalLink, X, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  detectInAppBrowser,
  isStandaloneMode,
  copyAppUrl,
  openInExternalBrowser,
  APP_URL,
} from '../utils/inappBrowser';

/**
 * 인앱 브라우저 설치 안내 팝업 (2026-09-12)
 *
 * 카카오톡으로 공유된 링크를 바로 누르면 카톡 인앱 브라우저가 열리는데,
 * 인앱 브라우저는 홈 화면 앱 설치(PWA)를 원천적으로 지원하지 않는다.
 * "설치하기"가 먹통이 되고 사용자는 앱이 고장난 줄 안다.
 *
 * 처음엔 화면 상단 배너로 만들었는데 로그인 화면을 밀어내고 지저분해서,
 * 화면 정중앙 팝업으로 바꾸고 설치 순서를 1-2-3 으로 명확히 번호 매겼다.
 *
 * 다른 화면(PWAInstaller 등)에서 다시 띄우고 싶으면
 *   window.dispatchEvent(new Event('handy:show-inapp-guide'))
 * 를 호출하면 된다.
 */

export const SHOW_INAPP_GUIDE_EVENT = 'handy:show-inapp-guide';

export function InAppBrowserNotice() {
  const [appName, setAppName] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isStandaloneMode()) return;      // 이미 앱으로 실행 중이면 안내 불필요
    const name = detectInAppBrowser();
    if (!name) return;
    setAppName(name);
    setOpen(true);

    const reopen = () => setOpen(true);
    window.addEventListener(SHOW_INAPP_GUIDE_EVENT, reopen);
    return () => window.removeEventListener(SHOW_INAPP_GUIDE_EVENT, reopen);
  }, []);

  if (!appName || !open) return null;

  const handleCopy = async () => {
    await copyAppUrl();
    setCopied(true);
    toast.success('주소를 복사했습니다.');
  };

  const handleOpenBrowser = async () => {
    // 브라우저로 넘어간 뒤 붙여넣을 수 있도록 복사도 같이 해둔다.
    await copyAppUrl();
    setCopied(true);
    const opened = openInExternalBrowser();
    if (!opened) {
      // iOS 인스타/라인 등: 외부 브라우저를 강제로 열 표준 방법이 없다.
      toast.info('사파리를 직접 열고 주소창에 붙여넣어 주세요.', { duration: 6000 });
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(17, 24, 39, 0.6)',
        backdropFilter: 'blur(3px)',
        WebkitBackdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '360px',
          maxHeight: '88vh',
          overflowY: 'auto',
          background: '#FFFFFF',
          borderRadius: '24px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.30)',
        }}
      >
        {/* ── 헤더 ─────────────────────────────── */}
        <div
          style={{
            position: 'relative',
            background: 'linear-gradient(135deg, #FF6B00 0%, #FF8C00 100%)',
            padding: '24px 20px 20px',
            textAlign: 'center',
            borderRadius: '24px 24px 0 0',
          }}
        >
          <button
            onClick={() => setOpen(false)}
            aria-label="닫기"
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              padding: '6px',
              borderRadius: '999px',
              background: 'rgba(255,255,255,0.22)',
              color: '#FFFFFF',
              lineHeight: 0,
            }}
          >
            <X className="w-4 h-4" />
          </button>

          <div
            style={{
              width: '56px',
              height: '56px',
              margin: '0 auto 12px',
              borderRadius: '50%',
              background: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            }}
          >
            <AlertTriangle className="w-7 h-7" style={{ color: '#FF6B00' }} />
          </div>

          <h2 style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 800, lineHeight: 1.35 }}>
            {appName}에서는
            <br />
            앱 설치가 되지 않습니다
          </h2>
          <p style={{ color: '#FFE4CC', fontSize: '13px', marginTop: '6px', fontWeight: 500 }}>
            아래 3단계만 따라하면 설치돼요
          </p>
        </div>

        {/* ── 설치 순서 ────────────────────────── */}
        <div style={{ padding: '20px 18px 18px' }}>
          {/* 1단계 : 주소 복사 */}
          <Step
            num={1}
            done={copied}
            title="앱 주소 복사하기"
            desc="아래 버튼을 누르면 주소가 복사됩니다."
            last={false}
          >
            <div
              style={{
                marginTop: '8px',
                padding: '9px 12px',
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '10px',
                fontSize: '12px',
                color: '#6B7280',
                wordBreak: 'break-all',
              }}
            >
              {APP_URL}
            </div>
            <button
              onClick={handleCopy}
              style={{
                marginTop: '8px',
                width: '100%',
                padding: '11px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '7px',
                background: copied ? '#ECFDF5' : '#FFF4E8',
                color: copied ? '#15803D' : '#E85D00',
                border: copied ? '1px solid #A7F3D0' : '1px solid #FFD9B8',
              }}
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? '주소가 복사됐어요' : '주소 복사하기'}
            </button>
          </Step>

          {/* 2단계 : 크롬·사파리 열기 */}
          <Step
            num={2}
            done={false}
            title="크롬·사파리 브라우저 열기"
            desc="버튼을 누르면 브라우저로 바로 이동합니다."
            last={false}
          >
            <button
              onClick={handleOpenBrowser}
              style={{
                marginTop: '8px',
                width: '100%',
                padding: '13px',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                background: 'linear-gradient(135deg, #FF6B00 0%, #FF8C00 100%)',
                color: '#FFFFFF',
                boxShadow: '0 4px 12px rgba(255,107,0,0.32)',
              }}
            >
              <ExternalLink className="w-[18px] h-[18px]" />
              브라우저로 열기
            </button>
          </Step>

          {/* 3단계 : 설치 실행 */}
          <Step
            num={3}
            done={false}
            title="접속 후 “설치하기” 누르기"
            desc="브라우저가 열리지 않으면 크롬을 직접 실행하고, 주소창에 복사한 주소를 붙여넣어 접속하세요. 화면 하단의 “설치하기”를 누르면 홈 화면에 앱이 설치됩니다."
            last={true}
          />

          <button
            onClick={() => setOpen(false)}
            style={{
              marginTop: '16px',
              width: '100%',
              padding: '11px',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: 600,
              color: '#6B7280',
              background: '#F3F4F6',
            }}
          >
            설치 안 하고 둘러보기
          </button>
        </div>
      </div>
    </div>
  );
}

/** 번호가 붙은 설치 단계 한 줄. 왼쪽 세로선으로 1-2-3 순서를 시각적으로 잇는다. */
function Step({
  num,
  done,
  title,
  desc,
  last,
  children,
}: {
  num: number;
  done: boolean;
  title: string;
  desc: string;
  last: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', gap: '12px' }}>
      {/* 번호 + 연결선 */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <div
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            background: done ? '#16A34A' : '#FF6B00',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 800,
            lineHeight: 1,
          }}
        >
          {done ? <Check className="w-4 h-4" /> : num}
        </div>
        {!last && <div style={{ flex: 1, width: '2px', background: '#F1E3D6', marginTop: '4px' }} />}
      </div>

      {/* 내용 */}
      <div style={{ flex: 1, minWidth: 0, paddingBottom: last ? 0 : '18px' }}>
        <p style={{ fontSize: '14px', fontWeight: 700, color: '#111827', lineHeight: 1.4 }}>{title}</p>
        <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '3px', lineHeight: 1.55 }}>{desc}</p>
        {children}
      </div>
    </div>
  );
}
